import React, { useEffect, useRef, useState } from 'react';
import { ArrowRight, Check } from 'lucide-react';
import { Button } from '../ui/button';
import { products, testimonials, clients } from '../../data/mock';

// Shared scroll reveal hook
const useReveal = (threshold = 0.12) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold }
    );
    const el = ref.current;
    if (el) observer.observe(el);
    return () => { if (el) observer.unobserve(el); };
  }, [threshold]);

  return [ref, isVisible];
};

// Star Rating
const StarRating = ({ count, isVisible }) => (
  <div className="flex mb-6 gap-0.5">
    {Array.from({ length: count }).map((_, i) => (
      <svg
        key={i}
        className={`w-5 h-5 text-yellow-400 fill-current star-animate ${isVisible ? 'visible' : ''} star-delay-${i + 1}`}
        viewBox="0 0 20 20"
      >
        <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
      </svg>
    ))}
  </div>
);

/* ====== PRODUCTS ====== */
export const ProductsSection = () => {
  const [headerRef, headerVisible] = useReveal();

  return (
    <section id="services" className="py-32">
      <div className="container mx-auto px-6">
        <div ref={headerRef} className={`reveal ${headerVisible ? 'visible' : ''}`}>
          <h2 className="text-5xl md:text-7xl font-bold text-center mb-6">Our Products</h2>
          <p className="text-xl text-gray-600 text-center mb-24 max-w-3xl mx-auto reveal-delay-1">
            Complete printing solutions for all your business needs
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {products.map((product, i) => (
            <ProductCard key={product.id} product={product} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};

const ProductCard = ({ product, index }) => {
  const [ref, isVisible] = useReveal(0.08);
  const delayClass = `reveal-delay-${Math.min((index % 3) + 1, 3)}`;

  return (
    <div ref={ref} className={`reveal ${delayClass} ${isVisible ? 'visible' : ''}`}>
      <div className="card-animate rounded-xl p-6 shadow-md bg-gradient-to-br from-white to-gray-50/80 border border-gray-100/60">
        <div className="aspect-video mb-4 rounded-lg overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100">
          <img
            src={product.image}
            alt={product.name}
            className="w-full h-full object-contain"
            loading="lazy"
          />
        </div>
        <h3 className="text-xl font-bold mb-2">{product.name}</h3>
        <p className="text-gray-600 text-sm">{product.description}</p>
      </div>
    </div>
  );
};

/* ====== TESTIMONIALS ====== */
export const TestimonialsSection = () => {
  const [headerRef, headerVisible] = useReveal();

  return (
    <section className="py-32 bg-gradient-to-b from-white to-gray-50">
      <div className="container mx-auto px-6">
        <div ref={headerRef} className={`reveal ${headerVisible ? 'visible' : ''}`}>
          <h2 className="text-5xl md:text-7xl font-bold text-center mb-8">What Our Clients Say</h2>
          <p className="text-xl text-gray-600 text-center mb-24 max-w-3xl mx-auto">
            Trusted by businesses across India for exceptional quality and service
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {testimonials.map((t, i) => (
            <TestimonialCard key={t.id} testimonial={t} index={i} />
          ))}
        </div>

        <TrustBadge />
      </div>
    </section>
  );
};

const TestimonialCard = ({ testimonial, index }) => {
  const [ref, isVisible] = useReveal(0.08);

  return (
    <div ref={ref} className={`reveal reveal-delay-${index + 1} ${isVisible ? 'visible' : ''}`}>
      <div className="card-animate rounded-xl p-8 shadow-md bg-white border border-gray-100/60 h-full">
        <StarRating count={testimonial.stars} isVisible={isVisible} />
        <p className="text-lg text-gray-700 leading-relaxed mb-6 italic">
          &ldquo;{testimonial.quote}&rdquo;
        </p>
        <div className="pt-6 border-t border-gray-100">
          <p className="font-bold text-lg text-black">{testimonial.name}</p>
          <p className="text-gray-500 text-sm mt-1">{testimonial.title}</p>
        </div>
      </div>
    </div>
  );
};

const TrustBadge = () => {
  const [ref, isVisible] = useReveal();
  return (
    <div ref={ref} className={`reveal ${isVisible ? 'visible' : ''}`}>
      <div className="text-center mt-16">
        <div className="inline-flex items-center gap-3 bg-gradient-to-r from-blue-50 to-purple-50 px-8 py-4 rounded-full">
          <Check className="w-6 h-6 text-green-600 icon-hover" />
          <span className="text-lg font-semibold">Trusted by top corporates across India</span>
        </div>
      </div>
    </div>
  );
};

/* ====== CLIENTS CAROUSEL ====== */
export const ClientsSection = () => {
  const [headerRef, headerVisible] = useReveal();
  const doubledClients = [...clients, ...clients];

  return (
    <section className="py-20 bg-white overflow-hidden">
      <div className="container mx-auto px-6">
        <div ref={headerRef} className={`reveal ${headerVisible ? 'visible' : ''}`}>
          <h2 className="text-4xl md:text-6xl font-bold text-center mb-16">Our Esteemed Clients</h2>
        </div>
      </div>

      <div className="relative">
        <div className="flex overflow-hidden">
          <div className="carousel-track" style={{ paddingRight: '64px' }}>
            {doubledClients.map((client, i) => (
              <div
                key={`${client.id}-${i}`}
                className="card-animate flex-shrink-0 w-80 h-48 bg-white rounded-2xl shadow-lg border border-gray-100 flex items-center justify-center p-8"
              >
                <img
                  src={client.logo}
                  alt={client.name}
                  className="max-w-full max-h-full object-contain"
                  loading="lazy"
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      <p className="text-center mt-12 text-gray-500 text-lg">
        Trusted by leading financial institutions across India
      </p>
    </section>
  );
};

/* ====== CTA ====== */
export const CTASection = () => {
  const [ref, isVisible] = useReveal();

  const handleGetStarted = () => {
    window.open('https://mpprinterscontact.vercel.app/', '_blank');
  };

  return (
    <section id="contact" className="py-32 bg-black text-white">
      <div className="container mx-auto px-6 text-center">
        <div ref={ref} className={`reveal ${isVisible ? 'visible' : ''}`}>
          <h2 className="text-5xl md:text-7xl font-bold mb-8">Start Your First Print Today</h2>
          <p className="text-xl text-gray-400 mb-12 max-w-2xl mx-auto">
            Join thousands of satisfied customers who trust us with their printing needs
          </p>
          <Button
            onClick={handleGetStarted}
            className="btn-animate bg-white text-black hover:bg-gray-100 text-lg px-12 py-6 rounded-full"
          >
            Get Started
            <ArrowRight className="ml-2" />
          </Button>
        </div>
      </div>
    </section>
  );
};
